import ApiErrorBanner from '@/components/ApiErrorBanner';
import { errorToast, successToast } from '@/helpers.tsx';
import { type DataTableTemplateApiId } from '@/model/DataTable.model.ts';
import InVivoError from '@/model/InVivoError.ts';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { ErrorMessage } from '@hookform/error-message';
import { type FC } from 'react';
import { useForm } from 'react-hook-form';

export interface RenameDataTableTemplateProps {
  dataTableTemplateName: string;
  dataTableTemplateApiId: DataTableTemplateApiId;
  onTemplateRename: () => void;
  closeModal: () => void;
}

interface FormValues {
  name: string;
}

const RenameDataTableTemplate: FC<RenameDataTableTemplateProps> = ({
  dataTableTemplateName,
  dataTableTemplateApiId,
  closeModal,
  onTemplateRename,
}) => {
  return (
    <ModalContainer size="narrow">
      <ModalHeader title="Edit Template name" closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />
      <RenameDataTableTemplateForm
        dataTableTemplateName={dataTableTemplateName}
        dataTableTemplateApiId={dataTableTemplateApiId}
        onTemplateRename={onTemplateRename}
        closeModal={closeModal}
      />
    </ModalContainer>
  );
};

const RenameDataTableTemplateForm: FC<RenameDataTableTemplateProps> = ({
  dataTableTemplateName,
  dataTableTemplateApiId,
  onTemplateRename,
  closeModal,
}) => {
  const {
    invoke: patchTemplate,
    response: templatePATCHResponse,
    loading: patchingTemplate,
  } = useApiHook({
    endpoint: 'PATCH /api/v1/datatables/templates/{dataTableTemplateApiId}/rename',
    path: { dataTableTemplateApiId },
    invokeOnInit: false,
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { name: dataTableTemplateName },
  });
  const onSubmit = async (formValues: FormValues): Promise<void> => {
    try {
      await patchTemplate({
        path: { dataTableTemplateApiId },
        body: formValues,
      });
      onTemplateRename();
      closeModal();
      successToast('Template updated successfully');
    } catch (err) {
      const ivError = new InVivoError('Could not rename data table template', {
        slug: 'rename-data-table-template',
        cause: err,
      });
      ExceptionHandler.captureException(ivError);
      errorToast('Could not rename data table template');
    }
  };
  return (
    <form>
      {templatePATCHResponse?.type === 'error' && <ApiErrorBanner />}
      <div className="pa3">
        <label>Name</label>
        <input
          type="text"
          id="name"
          data-testid={'template-name-input'}
          style={{ marginBottom: 0 }}
          {...register('name', {
            required: 'Name is required',
            maxLength: { value: 255, message: 'Maximum of 255 characters' },
          })}
          autoFocus
        />
        <ErrorMessage
          errors={errors}
          name="name"
          render={({ message }) => <small className="red db pt2">{message}</small>}
        />
      </div>
      <ModalActions
        submitButtonProps={{ disabled: patchingTemplate || templatePATCHResponse?.type === 'error' }}
        onSubmit={handleSubmit(onSubmit)}
        onCancel={closeModal}
        className="pa3 bt b--moon-gray"
      />
    </form>
  );
};

export default RenameDataTableTemplate;
